import { InjectionToken, Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { AuthStore, SyncedCollection, type ListenFn } from '@huddle-sports-platform/app-core';
import { invitationSchema, type Invitation } from '@huddle-sports-platform/schema';
import { collection, query, where, type Query } from 'firebase/firestore';

import { normaliseEmail } from '../core/email';
import { db } from '../core/firebase';
import { listMyInvitations, type MyInvitation } from './callables';
import { ClubBrandStore } from './club-brand';

/**
 * The notice waiting for the signed-in person - LIVE.
 *
 * A listener, not a fetch, so an invitation raised in HQ while somebody is
 * using the app reaches them at once, and one that is withdrawn disappears
 * instead of failing under their thumb when they tap OK.
 *
 * THE QUERY IS THE SECURITY STORY. Rules judge a query by its CONSTRAINTS,
 * never by the documents it would return, so `where('email', '==', mine)` is
 * precisely what makes this readable - an unpinned query over the invite
 * book, or one pinned to somebody else's address, is refused. Those three
 * cases are pinned in huddle-functions/test/rules.test.ts because this store
 * depends on them. A single equality also needs no composite index, which is
 * why `status` is filtered here rather than in the query.
 *
 * Club identity (name, crest, colours) comes from the LOCAL brand, not the
 * server: the invitation document carries none, and `listMyInvitations`
 * existed to join it on. In a white-labelled build we already know whose app
 * this is, so the Callable is now only the fallback for a listener that
 * cannot attach.
 *
 * Scoped to THIS build's club, always. A white-labelled app must never
 * mention that another club exists, let alone that its member was invited
 * to one.
 */
/**
 * How the store attaches its listener. Undefined = Firestore's own
 * `onSnapshot` (the kit's default). A spec provides a fake to drive the
 * snapshot stream without a browser - the same seam the kit documents on
 * `SyncedCollection`.
 */
export const ADMIN_INVITE_LISTEN = new InjectionToken<ListenFn | undefined>(
  'admin invite listen',
  { providedIn: 'root', factory: () => undefined },
);

/**
 * WHAT to listen to - the one query in this app whose exact shape is a
 * security contract (see the class note). Injectable purely so a spec can
 * hand back a stub without booting Firebase; the default is the real thing
 * and `db()` is only touched when it actually attaches.
 */
export const ADMIN_INVITE_SCOPE = new InjectionToken<(clubId: string, email: string) => Query>(
  'admin invite scope',
  {
    providedIn: 'root',
    factory:
      () => (clubId: string, email: string) =>
        query(collection(db(), 'clubs', clubId, 'invitations'), where('email', '==', email)),
  },
);

@Injectable({ providedIn: 'root' })
export class AdminInviteStore {
  private readonly auth = inject(AuthStore);
  private readonly clubBrand = inject(ClubBrandStore);

  private readonly sync = new SyncedCollection<Invitation>(
    invitationSchema,
    'clubs/{id}/invitations',
    inject(ADMIN_INVITE_LISTEN),
  );

  /** The query shape - see ADMIN_INVITE_SCOPE. */
  private readonly scope = inject(ADMIN_INVITE_SCOPE);

  /** What the fallback Callable found, when the listener could not attach. */
  private readonly fetched = signal<MyInvitation | null>(null);

  /**
   * The offer, or null - shaped for the page, with the club filled in from
   * the local brand. Only unanswered, unexpired ones count: the listener
   * sees the document through every status it passes.
   */
  readonly invitation = computed<MyInvitation | null>(() => {
    const brand = this.clubBrand.brand();
    if (!brand) return null;
    const live = [...this.sync.docs().entries()].find(
      ([, invite]) =>
        invite.status === 'pending' && (invite.expiresAt == null || invite.expiresAt > Date.now()),
    );
    if (!live) return this.fetched();
    const [invitationId, invite] = live;
    return {
      clubId: brand.clubId,
      invitationId,
      clubName: brand.appName,
      sport: '',
      logoUrl: brand.logoUrl,
      primaryColour: brand.primaryColour,
      secondaryColour: brand.secondaryColour,
      memberTypes: invite.memberTypes,
      roleKeys: invite.roleKeys,
      expiresAt: invite.expiresAt,
    };
  });

  /** Applied (or found dead) this session. The listener delivers the same
   *  news a moment later; this makes it count IMMEDIATELY, so the guard on
   *  the next route cannot read a stale notice. */
  private readonly answered = signal(false);

  /** There is a notice waiting to be seen. */
  readonly pending = computed(() => !!this.invitation() && !this.answered());

  /** Whose answer the store holds - `undefined` = nobody's yet. */
  private loadedFor: string | null | undefined;
  private inflight: Promise<void> | null = null;

  constructor() {
    // Re-scope whenever the login or the club changes. The FIRST attach is
    // imperative (whenLoaded) instead: guards run during the very first
    // navigation, before Angular flushes root effects, and a guard waiting
    // on an effect deadlocks the router into a blank page.
    effect(() => {
      const uid = this.auth.user()?.uid ?? null;
      const clubId = this.clubBrand.brand()?.clubId ?? null;
      untracked(() => {
        if (this.loadedFor === undefined) return; // whenLoaded owns the first one
        if (uid === this.loadedFor) return;
        void this.attach(uid, clubId);
      });
    });
  }

  /** Resolves once THIS account's answer is in, attaching if nobody has. */
  whenLoaded(): Promise<void> {
    const uid = this.auth.user()?.uid ?? null;
    if (this.loadedFor === uid && !this.inflight) return Promise.resolve();
    if (this.inflight) return this.inflight;
    this.inflight = this.attach(uid, this.clubBrand.brand()?.clubId ?? null).finally(() => {
      this.inflight = null;
    });
    return this.inflight;
  }

  private async attach(uid: string | null, clubId: string | null): Promise<void> {
    const address = this.auth.user()?.email;
    const email = address ? normaliseEmail(address) : null;
    if (!uid || !email) {
      // Signed out, or a login with no address: nobody to be invited.
      this.reset(uid);
      this.sync.detach();
      return;
    }
    // On the web the club is resolved from the address, asynchronously, so
    // the brand can still be in flight the first time somebody asks. Wait
    // for it: answering "no invitation" here and REMEMBERING that answer is
    // what made a fresh sign-up walk past its own invite until a reload.
    if (!clubId) {
      await this.whenBranded();
      clubId = this.clubBrand.brand()?.clubId ?? null;
    }
    this.reset(uid);
    if (!clubId) {
      // A genuinely brandless build (the shared dev app) - nothing to watch.
      this.sync.detach();
      return;
    }
    this.sync.warm(this.scope(clubId, email));
    await this.settled();
    // A listener that never attached (rules, transport, an old client) must
    // not silently swallow an invitation - ask the server the old way.
    if (this.sync.failed()) await this.fetchFallback(clubId);
  }

  /** The club lookup, when the address is still being resolved (web). */
  private whenBranded(): Promise<void> {
    if (this.clubBrand.settled()) return Promise.resolve();
    return new Promise((resolve) => {
      const timer = setInterval(() => {
        if (this.clubBrand.settled()) {
          clearInterval(timer);
          resolve();
        }
      }, 25);
    });
  }

  /** The listener's first SERVER snapshot, or its failure. A cached one
   *  does not count - an empty cache would read as "no invitation". */
  private settled(): Promise<void> {
    return new Promise((resolve) => {
      const timer = setInterval(() => {
        if (this.sync.failed() || (this.sync.attached() && !this.sync.warming())) {
          clearInterval(timer);
          resolve();
        }
      }, 25);
    });
  }

  private async fetchFallback(clubId: string): Promise<void> {
    try {
      const { invitations } = await listMyInvitations({ clubId });
      this.fetched.set(invitations[0] ?? null);
    } catch {
      // An offer nobody can read is not a reason to hold somebody out of
      // their club. They are asked again next launch.
      this.fetched.set(null);
    }
  }

  /** A new account's answer replaces the last one. */
  private reset(uid: string | null): void {
    this.loadedFor = uid;
    this.answered.set(false);
    this.fetched.set(null);
  }

  /**
   * Applied (or found dead) - the notice is settled from this instant.
   *
   * The page navigates immediately afterwards, and the guard on the
   * destination would otherwise race the snapshot carrying the new status.
   * The listener still delivers it; this just refuses to be behind.
   */
  async refresh(): Promise<void> {
    this.answered.set(true);
    const clubId = this.clubBrand.brand()?.clubId;
    if (clubId && this.sync.failed()) await this.fetchFallback(clubId);
  }
}
